import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type PaymentRevealPhase = 'loading' | 'paused' | 'morphed' | 'revealed';

const PAUSE_MS = 650; 
const MORPH_HOLD_MS = 700;
const FLY_DURATION_MS = 650;

function prefersReducedMotion() {
	return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export function usePaymentResultReveal(isTerminal: boolean) {
	const [phase, setPhase] = useState<PaymentRevealPhase>('loading');
	const [landed, setLanded] = useState(false);
	const standaloneIconRef = useRef<HTMLDivElement>(null);
	const finalIconRef = useRef<HTMLDivElement>(null);
	const capturedRectRef = useRef<DOMRect | null>(null);

	useEffect(() => {
		if (isTerminal && phase === 'loading') {
			setPhase('paused');
		}
	}, [isTerminal, phase]);

	useEffect(() => {
		if (phase !== 'paused') return;

		const timeout = setTimeout(() => setPhase('morphed'), prefersReducedMotion() ? 0 : PAUSE_MS);
		return () => clearTimeout(timeout);
	}, [phase]);

	useEffect(() => {
		if (phase !== 'morphed') return;

		if (standaloneIconRef.current) {
			capturedRectRef.current = standaloneIconRef.current.getBoundingClientRect();
		}

		const timeout = setTimeout(() => setPhase('revealed'), prefersReducedMotion() ? 0 : MORPH_HOLD_MS);
		return () => clearTimeout(timeout);
	}, [phase]);

	useLayoutEffect(() => {
		if (phase !== 'revealed') return;

		const finalEl = finalIconRef.current;
		const first = capturedRectRef.current;
		if (!finalEl || !first || prefersReducedMotion()) return;

		const last = finalEl.getBoundingClientRect();
		const dx = first.left + first.width / 2 - (last.left + last.width / 2);
		const dy = first.top + first.height / 2 - (last.top + last.height / 2);
		const scale = last.width === 0 ? 1 : first.width / last.width;

		finalEl.style.transition = 'none';
		finalEl.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;

		requestAnimationFrame(() => {
			finalEl.style.transition = `transform ${FLY_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
			finalEl.style.transform = 'translate(0px, 0px) scale(1)';
		});
	}, [phase]);

	useEffect(() => {
		if (phase !== 'revealed') return;

		const timeout = setTimeout(() => setLanded(true), prefersReducedMotion() ? 0 : FLY_DURATION_MS);
		return () => clearTimeout(timeout);
	}, [phase]);

	return { phase, landed, standaloneIconRef, finalIconRef };
}
